import React, { useState } from 'react';
import { Pencil, Trash2, Plus, Check, ExternalLink } from 'lucide-react';
import { Regimen } from '../../types/data';
import { saveCurrentPlan } from '../../utils/storage/currentPlans';
import { parseDurationYears } from '../../utils/plans';
import { GenericTable } from '../GenericTable/GenericTable';

interface RegimenListProps {
  regimens: Regimen[];
  onEdit: (regimen: Regimen) => void;
  onDelete: (id: number) => void;
}

export const RegimenList: React.FC<RegimenListProps> = ({
  regimens,
  onEdit,
  onDelete,
}) => {
  const [animatingRegimens, setAnimatingRegimens] = useState<Set<number>>(new Set());

  const handleSubscribe = (regimen: Regimen) => {
    const clonedRegimen = { ...regimen };
    delete clonedRegimen.id;
    saveCurrentPlan(clonedRegimen);
    setAnimatingRegimens((prev) => new Set(prev).add(regimen.id!));
    setTimeout(() => {
      setAnimatingRegimens((prev) => {
        const newSet = new Set(prev);
        newSet.delete(regimen.id!);
        return newSet;
      });
    }, 2000);
  };

  if (regimens.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No regimens found. Create one to get started.
      </div>
    );
  }

  return (
    <GenericTable
      data={regimens}
      columns={[
        { key: 'name', label: 'Name', sticky: 'left' },
        { key: 'type', label: 'Type' },
        {
          key: 'dosage',
          label: 'Dosage',
          render: (item) =>
            `${item.dosage ?? 'N/A'} ${item.dosage_units ?? ''}`.trim(),
        },
        // {
        //   key: 'desc',
        //   label: 'Regimen and Dose Description',
        //   render: (item) => generatePlanDescription(item),
        // },
        {
          key: 'doses_per_interval',
          label: 'Doses',
          render: (item) => item.doses_per_interval ?? 'N/A',
        },
        {
          key: 'frequency_interval',
          label: 'Frequency',
          render: (item) => item.frequency_interval ?? 'N/A',
        },
        { key: 'timing', label: 'Timing' },
        {
          key: 'cycle',
          label: 'Cycle',
          render: (item) =>
            item.days_on
              ? `${item.days_on} ${item.days_on === 1 ? 'day' : 'days'} on, ${
                  item.days_off || 0
                } ${item.days_off === 1 ? 'day' : 'days'} off`
              : 'Continuous',
        },
        {
          key: 'duration',
          label: 'Duration',
          render: (item) =>
            parseDurationYears(item.duration_period) >= 1 &&
            (!item.duration_repeat || item.duration_repeat === '0 days')
              ? 'Use as needed'
              : `${item.duration_period}${
                  item.duration_repeat && item.duration_repeat !== '0 days'
                    ? `, repeat every ${item.duration_repeat}`
                    : ''
                }`,
        },
        {
          key: 'dosage_level',
          label: 'Dosage Level',
          render: (item) => item.dosage_level ?? 'N/A',
        },
        {
          key: 'dosage_type',
          label: 'Dosage Type',
          render: (item) => item.dosage_type ?? 'N/A',
        },
        // {
        //   key: 'notes',
        //   label: 'Notes',
        //   render: (item) => item.notes ?? 'N/A',
        // },
        {
          key: 'source',
          label: 'Source',
          render: (item) =>
            item.source ? (
              <a
                href={item.source}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ExternalLink />
              </a>
            ) : (
              'N/A'
            ),
        },
        {
          key: 'actions',
          label: 'Actions',
          sticky: 'right',
          render: (item) => (
            <div className="flex gap-3">
              <button
                onClick={() => onEdit(item)}
                className="text-blue-600 hover:text-blue-800 transition-colors"
                title="Edit regimen"
              >
                <Pencil className="w-5 h-5" />
              </button>
              <button
                onClick={() => onDelete(item.id!)}
                className="text-red-600 hover:text-red-800 transition-colors"
                title="Delete regimen"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleSubscribe(item)}
                disabled={animatingRegimens.has(item.id!)}
                className={`text-green-600 hover:text-green-800 transition-colors ${
                  animatingRegimens.has(item.id!)
                    ? 'animate-pulse opacity-50 cursor-progress'
                    : ''
                }`}
                title="Add to Current Plan"
              >
                {animatingRegimens.has(item.id!) ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Plus className="w-5 h-5" />
                )}
              </button>
            </div>
          ),
        },
      ]}
    />
  );
};
